import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * Publish cadence for the level. This drives React state, and whatever renders the meter renders
 * with it — at one update per animation frame the whole interview screen re-rendered 60 times a
 * second. 20fps is indistinguishable on a wave and a third of the work.
 */
const PUBLISH_MS = 50;
/** Ignore changes smaller than this: silence otherwise jitters the meter (and the tree) forever. */
const PUBLISH_EPSILON = 0.015;

/**
 * Real-time microphone amplitude, 0–1 (RMS over a Web Audio AnalyserNode). Opens its OWN
 * audio-only getUserMedia stream — separate from useCameraPreview (video-only, to avoid a
 * double mic prompt with SpeechRecognition) and separate from SpeechRecognition itself
 * (which owns its mic internally and exposes no amplitude). Used for the hardware-check
 * "Voice Level" meter and, during the interview, to drive the candidate-speaking background
 * wave. Client-side only — nothing here is ever sent to the server.
 */
export function useMicLevel(): {
  level: number;
  supported: boolean;
  active: boolean;
  denied: boolean;
  start: () => void;
  stop: () => void;
} {
  const supported =
    typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia && typeof AudioContext !== "undefined";
  const [level, setLevel] = useState(0);
  const [active, setActive] = useState(false);
  const [denied, setDenied] = useState(false);

  const streamRef = useRef<MediaStream | null>(null);
  const ctxRef = useRef<AudioContext | null>(null);
  const rafRef = useRef<number | null>(null);
  // Generation counter. getUserMedia is async, and stop() can land while it is still in flight —
  // without this the stream resolves into a hook that has already torn down, the tracks are never
  // released and the candidate's mic indicator stays lit for the rest of the session.
  const genRef = useRef(0);
  const openingRef = useRef(false);
  const lastPublishRef = useRef(0);
  const lastLevelRef = useRef(0);

  const stop = useCallback(() => {
    genRef.current += 1;
    openingRef.current = false;
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    void ctxRef.current?.close().catch(() => undefined);
    ctxRef.current = null;
    lastLevelRef.current = 0;
    setActive(false);
    setLevel(0);
  }, []);

  const start = useCallback(() => {
    // `openingRef` matters as much as `streamRef`: two starts before the first prompt resolves
    // would open two microphone streams, and only one of them would ever be closed.
    if (!supported || streamRef.current || openingRef.current) return;
    const gen = genRef.current;
    openingRef.current = true;
    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        openingRef.current = false;
        if (gen !== genRef.current) {
          stream.getTracks().forEach((t) => t.stop()); // stopped mid-open — release it immediately
          return;
        }
        streamRef.current = stream;
        const ctx = new AudioContext();
        ctxRef.current = ctx;
        const source = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 512;
        source.connect(analyser);
        const data = new Uint8Array(analyser.frequencyBinCount);

        const tick = () => {
          if (gen !== genRef.current) return; // torn down between frames
          analyser.getByteTimeDomainData(data);
          let sumSq = 0;
          for (let i = 0; i < data.length; i++) {
            const v = (data[i]! - 128) / 128;
            sumSq += v * v;
          }
          const rms = Math.sqrt(sumSq / data.length);
          // RMS for normal speech sits well under 1 — scale up so the meter uses its range.
          const next = Math.max(0, Math.min(1, rms * 3.2));
          // Sample every frame (accurate), publish rarely (cheap): setLevel re-renders every
          // consumer, and this hook runs for the whole interview.
          const now = performance.now();
          if (now - lastPublishRef.current >= PUBLISH_MS && Math.abs(next - lastLevelRef.current) >= PUBLISH_EPSILON) {
            lastPublishRef.current = now;
            lastLevelRef.current = next;
            setLevel(next);
          }
          rafRef.current = requestAnimationFrame(tick);
        };
        setActive(true);
        tick();
      })
      .catch(() => {
        openingRef.current = false;
        if (gen === genRef.current) setDenied(true);
      });
  }, [supported]);

  useEffect(() => () => stop(), [stop]);

  return useMemo(
    () => ({ level, supported, active, denied, start, stop }),
    [level, supported, active, denied, start, stop],
  );
}
